import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { makeApi, serverTransport, type Api } from "./api/client";
import type { Transport } from "@connectrpc/connect";

/**
 * The Grovedock server this app talks to: always the one that served it (its sign-in pages,
 * API and this app share one origin), and the session token from signing in, if any.
 */
export type Target = { kind: "server"; url: string; token: string | null };

interface Connection {
  target: Target;
  signedIn: boolean;
  /** Calls that are not about one repository: accounts, organisations, the inbox. */
  api: Api;
  /** Calls about one repository (`OWNER/NAME`). */
  repoApi(repo: string): Api;
  /** Revokes this browser's session token and forgets it. */
  signOut(): Promise<void>;
}

const Context = createContext<Connection | null>(null);
const KEY = "gd.console.session";

/** Where the API is: this page's origin (a dev build may point elsewhere with VITE_SERVER). */
export const SERVER = ((import.meta.env.VITE_SERVER as string | undefined) || location.origin).replace(/\/+$/, "");
/** The server's host name, for addresses shown to people (clone URLs, the account menu). */
export const SERVER_HOST = new URL(SERVER).host;

function readToken(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function writeToken(token: string | null) {
  try {
    if (token) localStorage.setItem(KEY, token);
    else localStorage.removeItem(KEY);
  } catch {
    // Storage may be unavailable (private mode); the session then lasts for this tab only.
  }
}

/** Whether a signed-out visitor may open this app path: organisations and repositories (public
 * ones show; the rest say they don't exist). Home, inbox, onboarding and settings need an account. */
export function browsable(path: string): boolean {
  const first = path.replace(/^#?\/?/, "").split(/[/?]/)[0] ?? "";
  return !["", "inbox", "welcome", "settings"].includes(first);
}

/** The session token a sign-in hands over in the fragment (`#gd_session=gd_…`), if any. */
export function sessionFromHash(hash: string): string | null {
  const m = /^#gd_session=((?:gd|pando)_[A-Za-z0-9_]{1,200})$/.exec(hash);
  return m ? m[1]! : null;
}

// After signing in, the sign-in pages send the browser back with the session in the fragment.
// Read it once, before the router sees the URL, keep it, and wipe it from the address bar and
// history. Older versions' saved connections are dropped: those people sign in again.
// Links from when the app routed by the fragment (`/#/acme-labs/web`) open the same page.
if (location.hash.startsWith("#/")) history.replaceState(null, "", location.hash.slice(1));
const handedOver = sessionFromHash(location.hash);
let sessionToken: string | null = handedOver ?? readToken();
if (handedOver) {
  writeToken(handedOver);
  // Sign-in returns to the page's own path (`/acme?tab=people`): keep it, drop the fragment.
  history.replaceState(null, "", location.pathname + location.search);
  try {
    sessionStorage.removeItem("gd.signin.next");
  } catch {
    // nothing to clear
  }
}
try {
  localStorage.removeItem("gd.console.connection");
} catch {
  // nothing to clean
}

export function ConnectionProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(sessionToken);

  const value = useMemo<Connection>(() => {
    const transports = new Map<string, Transport>();
    const transport = (repo?: string): Transport => {
      const key = repo ?? "";
      let t = transports.get(key);
      if (!t) transports.set(key, (t = serverTransport(SERVER, token, repo)));
      return t;
    };
    const apis = new Map<string, Api>();
    const repoApi = (repo: string) => {
      let a = apis.get(repo);
      if (!a) apis.set(repo, (a = makeApi(transport(repo))));
      return a;
    };
    return {
      target: { kind: "server", url: SERVER, token },
      signedIn: !!token,
      api: makeApi(transport()),
      repoApi,
      async signOut() {
        if (token) {
          // Revoke the session's own token, so a copy left anywhere stops working.
          const api = makeApi(serverTransport(SERVER, token));
          await api.platform
            .viewer({})
            .then((v) => (v.tokenId ? api.platform.revokeToken({ id: v.tokenId }) : undefined))
            .catch(() => undefined);
        }
        sessionToken = null;
        writeToken(null);
        setToken(null);
      },
    };
  }, [token]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useConnection(): Connection {
  const c = useContext(Context);
  if (!c) throw new Error("useConnection outside ConnectionProvider");
  return c;
}

/** The platform-level API (not about one repository). */
export const usePlatformApi = () => useConnection().api;
